import { useEffect, useRef, useState } from "react";
import { GLOSSARY_TOPIC_ID, helpTopic, type HelpTopic } from "./topics";

/**
 * Contextual help for the screen in view, plus the glossary.
 *
 * A drawer rather than a page, because the question it answers — "what am I
 * looking at" — is asked while looking at it, and navigating away to find out
 * loses the thing you were asking about.
 *
 * Nothing here is remembered: help is asked for, not configured, and a drawer
 * that reopened itself on every visit would be a nag rather than an answer.
 */
export function HelpButton({ topicId, label = "Help" }: { topicId: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const topic = helpTopic(topicId);

  // No topic, no button. An opener that reveals an empty panel reads as "there
  // is nothing to say about this screen" rather than as a gap in the content.
  if (topic === null) return null;

  return (
    <>
      <button
        type="button"
        className="rounded-sm border border-slate-300 px-2 py-0.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
        data-testid={`help-open-${topicId}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">?</span>
        <span className="sr-only">{label}</span>
      </button>
      {open ? <HelpDrawer topic={topic} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function HelpDrawer({ topic, onClose }: { topic: HelpTopic; onClose: () => void }) {
  const [shown, setShown] = useState<HelpTopic>(topic);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const glossary = helpTopic(GLOSSARY_TOPIC_ID);

  // Escape closes, as every dialog should. Bound on the document so it works
  // wherever focus happens to be inside the panel.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Move focus into the panel on open, so a keyboard user is not left behind on
  // the button while the content they asked for sits unreachable behind it.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="help-drawer-backdrop">
      {/* The scrim closes on click; it is not a focus trap, and the panel is
          deliberately non-modal — reading help while the page stays visible is
          the point. */}
      <button
        type="button"
        className="flex-1 bg-slate-900/20"
        aria-label="Close help"
        data-testid="help-scrim"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-label={`Help: ${shown.title}`}
        className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl outline-hidden"
        data-testid="help-drawer"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold" data-testid="help-title">
            {shown.title}
          </h2>
          <button
            type="button"
            className="rounded-sm border border-slate-300 px-2 py-0.5 text-sm hover:bg-slate-100"
            data-testid="help-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/*
          Rendered from markdown at build time by the `render-help-markdown`
          plugin. The source is authored in this repository and reviewed like
          code; it never carries anything a user typed or a loaded file
          supplied, and the deployed CSP is `script-src 'self'`.
        */}
        <div
          className="help-prose space-y-3 text-sm leading-relaxed text-slate-800"
          data-testid="help-body"
          dangerouslySetInnerHTML={{ __html: shown.html }}
        />

        {glossary !== null && shown.id !== glossary.id ? (
          <button
            type="button"
            className="mt-4 text-sm text-sky-700 underline"
            data-testid="help-open-glossary"
            onClick={() => setShown(glossary)}
          >
            Glossary: this tool's words and GTZenda's
          </button>
        ) : null}
        {glossary !== null && shown.id === glossary.id && topic.id !== glossary.id ? (
          <button
            type="button"
            className="mt-4 text-sm text-sky-700 underline"
            data-testid="help-back"
            onClick={() => setShown(topic)}
          >
            Back to {topic.title}
          </button>
        ) : null}
      </div>
    </div>
  );
}
